
import React, { useEffect, useState, useContext } from "react";
import {
  Box,
  Breadcrumbs,
  Button,
  Chip,
  Typography,
  Card,
  CardContent,
  Grid,
  CircularProgress,
  Divider,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Slide,
  AppBar,
  Toolbar,
  useMediaQuery,
  useTheme,
  Paper,
} from "@mui/material";
import { emphasize, styled } from "@mui/material/styles";
import HomeIcon from "@mui/icons-material/Home";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { MdEdit, MdDelete, MdLocalOffer, MdClose } from "react-icons/md";
import { FaArrowLeft, FaCalendar, FaShoppingBag } from "react-icons/fa";
import { BiPlay } from "react-icons/bi";
import { fetchDataFromApi, deleteData } from "../../utils/api";
import { MyContext } from "../../App";
import { useNavigate, useParams } from "react-router-dom";
import Carousel from "react-material-ui-carousel";

const StyledBreadcrumb = styled(Chip)(({ theme }) => {
  const backgroundColor =
    theme.palette.mode === "light"
      ? theme.palette.grey[100]
      : theme.palette.grey[800];
  return {
    backgroundColor,
    height: theme.spacing(3),
    color: theme.palette.text.primary,
    fontWeight: theme.typography.fontWeightRegular,
    "&:hover, &:focus": {
      backgroundColor: emphasize(backgroundColor, 0.06),
    },
    "&:active": {
      boxShadow: theme.shadows[1],
      backgroundColor: emphasize(backgroundColor, 0.12),
    },
  };
});

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const ViewDetails = () => {
  const { id } = useParams();
  const [offerBanner, setOfferBanner] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [selectedMediaType, setSelectedMediaType] = useState(null);
  const context = useContext(MyContext);
  const navigate = useNavigate();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));

  useEffect(() => {
    fetchOfferBannerDetails();
  }, [id]);

  // Helper function to check if URL is video
  const isVideoUrl = (url) => {
    if (!url) return false;
    const videoExtensions = ['mp4', 'avi', 'mov', 'wmv', 'flv', 'mkv', 'webm'];
    const extension = url.split('.').pop().toLowerCase().split('?')[0];
    return videoExtensions.includes(extension);
  };

  const fetchOfferBannerDetails = async () => {
    setLoading(true);
    try {
      const response = await fetchDataFromApi(`/api/homeOfferBanner/${id}`);
      if (response) {
        setOfferBanner(response);
        setOpen(true);
      }
    } catch (error) {
      console.error("Error fetching offer banner:", error);
      context.setAlertBox({
        open: true,
        error: true,
        msg: "Failed to fetch offer banner details",
      });
      navigate("/homeOfferBanners");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setTimeout(() => {
      navigate("/homeOfferBanners");
    }, 300);
  };

  const handleDelete = async () => {
    if (window.confirm("Are you sure you want to delete this offer banner?")) {
      try {
        await deleteData(`/api/homeOfferBanner/${id}`);
        context.setAlertBox({
          open: true,
          error: false,
          msg: "Offer banner deleted successfully",
        });
        handleClose();
      } catch (error) {
        console.error("Error deleting banner:", error);
        context.setAlertBox({
          open: true,
          error: true,
          msg: "Failed to delete offer banner",
        });
      }
    }
  };

  const handleEdit = () => {
    setOpen(false);
    setTimeout(() => {
      navigate(`/homeOfferBanners/edit/${id}`);
    }, 300);
  };

  const formatDate = (date) => {
    if (!date) return "N/A";
    return new Date(date).toLocaleDateString("en-US", {
      month: "long",
      day: "2-digit",
      year: "numeric",
    });
  };

  const handleMediaClick = (media) => {
    const isVideo = isVideoUrl(media);
    setSelectedMedia(media);
    setSelectedMediaType(isVideo ? 'video' : 'image');
  };

  if (loading) {
    return (
      <div className="right-content w-100">
        <Box className="text-center p-5">
          <CircularProgress />
          <Typography className="mt-3">Loading details...</Typography>
        </Box>
      </div>
    );
  }

  if (!offerBanner) {
    return null;
  }

  return (
    <>
      <Dialog
        fullScreen={fullScreen}
        open={open}
        onClose={handleClose}
        TransitionComponent={Transition}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: fullScreen ? 0 : 2,
            maxHeight: "90vh",
          },
        }}
      >
        <AppBar sx={{ position: "relative", backgroundColor: "#1976d2" }}>
          <Toolbar>
            <IconButton edge="start" color="inherit" onClick={handleClose} aria-label="close">
              <MdClose />
            </IconButton>
            <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
              Offer Banner Details
            </Typography>
            <Button
              autoFocus
              color="inherit"
              startIcon={<MdEdit />}
              onClick={handleEdit}
              sx={{ mr: 1 }}
            >
              Edit
            </Button>
            <Button
              autoFocus
              color="inherit"
              startIcon={<MdDelete />}
              onClick={handleDelete}
              sx={{ backgroundColor: "rgba(255,255,255,0.1)" }}
            >
              Delete
            </Button>
          </Toolbar>
        </AppBar>

        <DialogContent dividers sx={{ p: 3, backgroundColor: "#f5f5f5" }}>
          <Grid container spacing={3}>
            {/* Left Column - Images/Videos */}
            <Grid item xs={12} md={5}>
              <Paper elevation={3} sx={{ p: 2, borderRadius: 2 }}>
                <Typography variant="h6" className="mb-3" fontWeight={600}>
                  📸 Banner Media ({offerBanner.images?.length || 0})
                </Typography>

                {offerBanner.images && offerBanner.images.length > 0 ? (
                  <Box>
                    <Carousel
                      animation="slide"
                      indicators={offerBanner.images.length > 1}
                      navButtonsAlwaysVisible={offerBanner.images.length > 1}
                      autoPlay={false}
                      sx={{
                        borderRadius: 2,
                        overflow: "hidden",
                        backgroundColor: "#000",
                      }}
                    >
                      {offerBanner.images.map((media, index) => {
                        const isVideo = isVideoUrl(media);
                        return (
                          <Box
                            key={index}
                            sx={{
                              height: 400,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              cursor: "pointer",
                              backgroundColor: "#000",
                              position: "relative",
                            }}
                            onClick={() => handleMediaClick(media)}
                          >
                            {isVideo ? (
                              <>
                                <video
                                  src={media}
                                  style={{
                                    maxHeight: "100%",
                                    maxWidth: "100%",
                                    objectFit: "contain",
                                  }}
                                  controls
                                />
                                <Box
                                  sx={{
                                    position: "absolute",
                                    top: 10,
                                    right: 10,
                                    background: "rgba(156, 39, 176, 0.9)",
                                    color: "white",
                                    padding: "4px 10px",
                                    borderRadius: "12px",
                                    fontSize: "0.7rem",
                                    fontWeight: 700,
                                    letterSpacing: "0.5px",
                                  }}
                                >
                                  VIDEO
                                </Box>
                              </>
                            ) : (
                              <img
                                src={media}
                                alt={`Banner ${index + 1}`}
                                style={{
                                  maxHeight: "100%",
                                  maxWidth: "100%",
                                  objectFit: "contain",
                                }}
                              />
                            )}
                          </Box>
                        );
                      })}
                    </Carousel>

                    <Box className="mt-3">
                      <Grid container spacing={1}>
                        {offerBanner.images.map((media, index) => {
                          const isVideo = isVideoUrl(media);
                          return (
                            <Grid item xs={3} key={index}>
                              <Box
                                sx={{
                                  height: 80,
                                  borderRadius: 1,
                                  overflow: "hidden",
                                  cursor: "pointer",
                                  border: "2px solid #e0e0e0",
                                  position: "relative",
                                  "&:hover": {
                                    border: "2px solid #1976d2",
                                  },
                                }}
                                onClick={() => handleMediaClick(media)}
                              >
                                {isVideo ? (
                                  <>
                                    <video
                                      src={media}
                                      style={{
                                        width: "100%",
                                        height: "100%",
                                        objectFit: "cover",
                                      }}
                                    />
                                    <Box
                                      sx={{
                                        position: "absolute",
                                        top: "50%",
                                        left: "50%",
                                        transform: "translate(-50%, -50%)",
                                        background: "rgba(0, 0, 0, 0.6)",
                                        borderRadius: "50%",
                                        width: "30px",
                                        height: "30px",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                      }}
                                    >
                                      <BiPlay size={20} color="#ffffff" />
                                    </Box>
                                  </>
                                ) : (
                                  <img
                                    src={media}
                                    alt={`Thumbnail ${index + 1}`}
                                    style={{
                                      width: "100%",
                                      height: "100%",
                                      objectFit: "cover",
                                    }}
                                  />
                                )}
                              </Box>
                            </Grid>
                          );
                        })}
                      </Grid>
                    </Box>
                  </Box>
                ) : (
                  <Box className="text-center p-5">
                    <Typography color="textSecondary">No media available</Typography>
                  </Box>
                )}
              </Paper>
            </Grid>

            {/* Right Column - Details */}
            <Grid item xs={12} md={7}>
              {/* Title and Status */}
              <Paper elevation={3} sx={{ p: 3, borderRadius: 2, mb: 2 }}>
                <Box className="d-flex justify-content-between align-items-start mb-2">
                  <Typography variant="h4" fontWeight={700} color="primary">
                    {offerBanner.title}
                  </Typography>
                  <Chip
                    icon={<MdLocalOffer />}
                    label={offerBanner.isActive ? "Active" : "Inactive"}
                    color={offerBanner.isActive ? "success" : "default"}
                    sx={{ fontSize: "1rem", p: 2 }}
                  />
                </Box>

                {offerBanner.description && (
                  <Typography variant="body1" color="textSecondary" className="mt-2">
                    {offerBanner.description}
                  </Typography>
                )}
              </Paper>

              {/* Details Card */}
              <Paper elevation={3} sx={{ p: 3, borderRadius: 2, mb: 2 }}>
                <Typography variant="h6" className="mb-3" fontWeight={600}>
                  📋 Offer Information
                </Typography>
                <Divider className="mb-3" />

                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="caption" color="textSecondary" fontWeight={600}>
                        CREATED AT
                      </Typography>
                      <Typography variant="body1" fontWeight={500}>
                        <FaCalendar
                          className="mr-2"
                          style={{ verticalAlign: "middle", color: "#1976d2" }}
                        />
                        {formatDate(offerBanner.createdAt)}
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item xs={6}>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="caption" color="textSecondary" fontWeight={600}>
                        UPDATED AT
                      </Typography>
                      <Typography variant="body1" fontWeight={500}>
                        <FaCalendar
                          className="mr-2"
                          style={{ verticalAlign: "middle", color: "#1976d2" }}
                        />
                        {formatDate(offerBanner.updatedAt)}
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item xs={6}>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="caption" color="textSecondary" fontWeight={600}>
                        START DATE
                      </Typography>
                      <Typography variant="body1" fontWeight={500} color="success.main">
                        {formatDate(offerBanner.startDate)}
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item xs={6}>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="caption" color="textSecondary" fontWeight={600}>
                        END DATE
                      </Typography>
                      <Typography variant="body1" fontWeight={500} color="error.main">
                        {formatDate(offerBanner.endDate)}
                      </Typography>
                    </Box>
                  </Grid>

                  {offerBanner.brand && (
                    <Grid item xs={6}>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="caption" color="textSecondary" fontWeight={600}>
                          BRAND
                        </Typography>
                        <Box className="mt-1">
                          <Chip label={offerBanner.brand} color="primary" variant="outlined" />
                        </Box>
                      </Box>
                    </Grid>
                  )}

                  {offerBanner.discount > 0 && (
                    <Grid item xs={6}>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="caption" color="textSecondary" fontWeight={600}>
                          DISCOUNT
                        </Typography>
                        <Box className="mt-1">
                          <Chip
                            label={`${offerBanner.discount}% OFF`}
                            color="error"
                            sx={{ fontWeight: 700, fontSize: "1rem" }}
                          />
                        </Box>
                      </Box>
                    </Grid>
                  )}

                  {offerBanner.priority > 0 && (
                    <Grid item xs={6}>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="caption" color="textSecondary" fontWeight={600}>
                          PRIORITY
                        </Typography>
                        <Typography variant="h5" color="primary" fontWeight={700}>
                          #{offerBanner.priority}
                        </Typography>
                      </Box>
                    </Grid>
                  )}

                  {offerBanner.category && (
                    <Grid item xs={12}>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="caption" color="textSecondary" fontWeight={600}>
                          CATEGORY
                        </Typography>
                        <Box className="mt-1">
                          <Chip
                            label={offerBanner.category.name}
                            color="primary"
                            variant="outlined"
                            sx={{ fontSize: "0.9rem" }}
                          />
                        </Box>
                      </Box>
                    </Grid>
                  )}
                </Grid>
              </Paper>

              {/* Products Section */}
              <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
                <Box className="d-flex justify-content-between align-items-center mb-3">
                  <Typography variant="h6" fontWeight={600}>
                    <FaShoppingBag
                      className="mr-2"
                      style={{ verticalAlign: "middle", color: "#1976d2" }}
                    />
                    Selected Products
                  </Typography>
                  <Chip
                    label={`${offerBanner.products?.length || 0} Products`}
                    color="primary"
                    size="small"
                  />
                </Box>
                <Divider className="mb-3" />

                {offerBanner.products && offerBanner.products.length > 0 ? (
                  <Box sx={{ maxHeight: 400, overflowY: "auto" }}>
                    <List>
                      {offerBanner.products.map((product, index) => (
                        <Paper
                          key={product._id}
                          variant="outlined"
                          sx={{
                            mb: 2,
                            borderRadius: 2,
                            transition: "all 0.3s",
                            "&:hover": {
                              boxShadow: 3,
                              transform: "translateY(-2px)",
                            },
                          }}
                        >
                          <ListItem sx={{ p: 2 }}>
                            <Box
                              sx={{
                                mr: 2,
                                position: "relative",
                              }}
                            >
                              <Avatar
                                src={product.images?.[0]}
                                variant="rounded"
                                sx={{ width: 70, height: 70 }}
                              />
                              <Chip
                                label={index + 1}
                                size="small"
                                color="primary"
                                sx={{
                                  position: "absolute",
                                  top: -8,
                                  left: -8,
                                  fontWeight: 700,
                                }}
                              />
                            </Box>
                            <ListItemText
                              primary={
                                <Typography variant="body1" fontWeight={700}>
                                  {product.name}
                                </Typography>
                              }
                              secondary={
                                <Box className="mt-1">
                                  {product.brand && (
                                    <Typography variant="body2" color="textSecondary">
                                      Brand: {product.brand}
                                    </Typography>
                                  )}
                                  <Box className="d-flex align-items-center mt-1">
                                    <Typography
                                      variant="h6"
                                      color="primary"
                                      fontWeight={700}
                                      className="mr-2"
                                    >
                                      ₹{product.price}
                                    </Typography>
                                    {product.discount > 0 && (
                                      <Chip
                                        label={`${product.discount}% OFF`}
                                        color="error"
                                        size="small"
                                      />
                                    )}
                                  </Box>
                                  {product.category && (
                                    <Chip
                                      label={product.category.name}
                                      size="small"
                                      variant="outlined"
                                      className="mt-1"
                                    />
                                  )}
                                </Box>
                              }
                            />
                          </ListItem>
                        </Paper>
                      ))}
                    </List>
                  </Box>
                ) : (
                  <Box className="text-center p-5">
                    <FaShoppingBag size={50} color="#ccc" />
                    <Typography color="textSecondary" className="mt-3">
                      No products selected
                    </Typography>
                  </Box>
                )}
              </Paper>
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>

      {/* Media Zoom Dialog - Image or Video */}
      <Dialog
        open={Boolean(selectedMedia)}
        onClose={() => {
          setSelectedMedia(null);
          setSelectedMediaType(null);
        }}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: "rgba(0,0,0,0.9)",
            boxShadow: "none",
          },
        }}
      >
        <IconButton
          onClick={() => {
            setSelectedMedia(null);
            setSelectedMediaType(null);
          }}
          sx={{
            position: "absolute",
            right: 10,
            top: 10,
            color: "white",
            backgroundColor: "rgba(255,255,255,0.2)",
            zIndex: 9999,
            "&:hover": {
              backgroundColor: "rgba(255,255,255,0.3)",
            },
          }}
        >
          <MdClose />
        </IconButton>
        <DialogContent 
          sx={{ 
            p: 0, 
            display: "flex", 
            alignItems: "center", 
            justifyContent: "center",
            minHeight: "90vh",
          }}
        >
          {selectedMedia && (
            selectedMediaType === 'video' ? (
              <video
                src={selectedMedia}
                controls
                autoPlay
                style={{
                  maxWidth: "100%",
                  maxHeight: "90vh",
                  objectFit: "contain",
                }}
              />
            ) : (
              <img
                src={selectedMedia}
                alt="Zoomed"
                style={{
                  maxWidth: "100%",
                  maxHeight: "90vh",
                  objectFit: "contain",
                }}
              />
            )
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ViewDetails;

